import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
} from '@tanstack/react-query'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type OrganizationId,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import {
  getTerminals,
  getTerminal,
  registerTerminal,
  updateTerminal,
  deleteTerminal,
  cancelReaderAction,
} from './terminals.api'
import type {
  RegisterTerminalInput,
  TerminalAccountContext,
  UpdateTerminalInput,
} from './terminals.types'
import { stripeMigrationQueries } from '../stripe-connect/stripe-migration.query'

interface AccountKeyScope {
  organizationId?: OrganizationIdInput
  accountContext?: TerminalAccountContext | undefined
}

/** Its fetcher calls the endpoint itself rather than reading the key, so it needs the organization. */
interface AccountQueryScope extends AccountKeyScope {
  organizationId: OrganizationId
}

type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

export const terminalQueryKeys = {
  all: ({ organizationId }: { organizationId?: OrganizationIdInput } = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'terminals' }, { organizationId }),
  ],
  index: ({ organizationId, accountContext }: AccountKeyScope = {}) =>
    buildScopedAdminQueryKey(terminalQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'index',
      ...(accountContext ? { accountContext } : {}),
    }),
  show: ({
    organizationId,
    terminalId,
    accountContext,
  }: AccountKeyScope & { terminalId?: string | null } = {}) =>
    buildScopedAdminQueryKey(terminalQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'show',
      terminalId,
      ...(accountContext ? { accountContext } : {}),
    }),
}

export const terminalMutationKeys = {
  sendCardPaymentToReader: ['terminal', 'sendCardPaymentToReader'],
}

export const terminalQueries = {
  index: ({ organizationId, accountContext }: AccountQueryScope) =>
    queryOptions({
      queryKey: terminalQueryKeys.index({ organizationId, accountContext }),
      queryFn: () => getTerminals({ organizationId, accountContext }),
    }),

  show: ({
    organizationId,
    terminalId,
    accountContext,
  }: AccountQueryScope & { terminalId: string }) =>
    queryOptions({
      queryKey: terminalQueryKeys.show({ organizationId, terminalId, accountContext }),
      queryFn: () => getTerminal({ organizationId, terminalId, accountContext }),
    }),
}

export function useTerminals({
  organizationId,
  accountContext,
  ...params
}: AccountKeyScope & QueryParams<ReturnType<typeof terminalQueries.index>> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...terminalQueries.index({ organizationId: resolvedOrganizationId, accountContext }),
    ...params,
  })
}

export function useWarmTerminals({
  organizationId,
  ...params
}: { organizationId?: OrganizationIdInput } & QueryParams<
  ReturnType<typeof terminalQueries.index>
> = {}) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useQuery({
    ...terminalQueries.index({ organizationId: resolvedOrganizationId }),
    ...params,
  })
}

export function useTerminal(
  terminalId: string,
  {
    organizationId,
    accountContext,
    ...params
  }: AccountKeyScope & QueryParams<ReturnType<typeof terminalQueries.show>> = {}
) {
  const resolvedOrganizationId = useRouteOrganizationId(organizationId)

  return useSuspenseQuery({
    ...terminalQueries.show({ organizationId: resolvedOrganizationId, terminalId, accountContext }),
    ...params,
  })
}

export function useRegisterTerminalMutation({
  accountContext,
}: { accountContext?: TerminalAccountContext } = {}) {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (values: RegisterTerminalInput) =>
      registerTerminal({ organizationId, accountContext, ...values }),
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: terminalQueryKeys.all({ organizationId }) }),
        ...(accountContext === 'target'
          ? [queryClient.invalidateQueries(stripeMigrationQueries.status({ organizationId }))]
          : []),
      ]),
  })
}

export function useUpdateTerminalMutation({
  accountContext,
}: { accountContext?: TerminalAccountContext } = {}) {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (values: UpdateTerminalInput & { terminalId: string }) =>
      updateTerminal({ organizationId, accountContext, ...values }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: terminalQueryKeys.all({ organizationId }) }),
  })
}

const cleanupDeletedTerminalQueries = ({
  queryClient,
  organizationId,
  terminalId,
  accountContext,
}: {
  queryClient: QueryClient
  organizationId: OrganizationId
  terminalId: string
  accountContext: TerminalAccountContext | undefined
}) => {
  queryClient.removeQueries({
    queryKey: terminalQueryKeys.show({ organizationId, terminalId, accountContext }),
    exact: true,
  })

  return Promise.all([
    queryClient.invalidateQueries({ queryKey: terminalQueryKeys.all({ organizationId }) }),
    ...(accountContext === 'source' || accountContext === 'target'
      ? [queryClient.invalidateQueries(stripeMigrationQueries.status({ organizationId }))]
      : []),
  ])
}

export function useDeleteTerminalMutation({
  cleanupOnSuccess = true,
  accountContext,
  retire,
}: {
  cleanupOnSuccess?: boolean
  accountContext?: TerminalAccountContext
  retire?: boolean
} = {}) {
  const organizationId = useRouteOrganizationId()
  const queryClient = useQueryClient()
  const cleanupDeletedTerminal = (terminalId: string) =>
    cleanupDeletedTerminalQueries({ queryClient, organizationId, terminalId, accountContext })

  const mutation = useMutation({
    mutationFn: (terminalId: string) =>
      deleteTerminal({
        organizationId,
        terminalId,
        accountContext,
        ...(retire ? { retire: true } : {}),
      }),
    onSuccess: (_, terminalId) => {
      if (!cleanupOnSuccess) {
        return undefined
      }

      return cleanupDeletedTerminal(terminalId)
    },
  })

  return {
    ...mutation,
    cleanupDeletedTerminal,
  }
}

export function useCancelReaderActionMutation({
  accountContext,
}: { accountContext?: TerminalAccountContext } = {}) {
  const organizationId = useRouteOrganizationId()

  return useMutation({
    mutationFn: (terminalId: string) =>
      cancelReaderAction({ organizationId, terminalId, accountContext }),
  })
}
